import React from 'react'
import type { FileCardKind } from '../FileCard/FileCard.types'
import type {
  FilePreviewContent,
  FilePreviewPanelProps,
} from './FilePreviewPanel.types'
import { sanitizeDocumentHtml } from '@utils/sanitize-html.util'
import './FilePreviewPanel.css'

const TYPE_ICON: Record<FileCardKind, string> = {
  image: 'mgc_pic_2_fill',
  pdf: 'mgc_pdf_fill',
  dcm: 'mgc_pic_fill',
  document: 'mgc_document_2_fill',
  other: 'mgc_file_fill',
}

const FilePreviewPanel: React.FC<FilePreviewPanelProps> = ({
  fileName,
  meta,
  content,
  zoom = 100,
  hasPrev = true,
  hasNext = true,
  className = '',
  previewState,
  onClose,
  onDelete,
  onDownload,
  onDetails,
  onZoomIn,
  onZoomOut,
  onPrev,
  onNext,
  headerActions,
}) => {
  const isPreview = previewState != null

  const resolvedContent: FilePreviewContent =
    content ??
    (isPreview
      ? previewState === 'image'
        ? {
            kind: 'image',
            src: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=800&q=80',
            alt: 'Dental x-ray',
          }
        : previewState === 'pdf'
          ? {
              kind: 'placeholder',
              fileType: 'pdf',
              fileName: 'Insurance_EOB.pdf',
            }
          : previewState === 'placeholder'
            ? {
                kind: 'placeholder',
                fileType: 'dcm',
                fileName: 'Panoramic.dcm',
              }
            : previewState === 'document'
              ? {
                  kind: 'document',
                  html: '<h3>Referral letter</h3><p>Please evaluate the patient for orthodontic treatment.</p>',
                }
              : { kind: 'empty' }
      : { kind: 'empty' })

  const resolvedName =
    fileName ??
    (resolvedContent.kind === 'placeholder'
      ? resolvedContent.fileName
      : resolvedContent.kind === 'empty'
        ? undefined
        : 'Bitewing_left.png')

  const resolvedMeta =
    meta ??
    (resolvedContent.kind === 'empty' ? undefined : 'Mar 12, 2026 - (1.2 MB)')

  const classNames = [
    'ds-file-preview',
    isPreview ? 'ds-file-preview--preview' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classNames}>
      <header className="ds-file-preview__header">
        <div className="ds-file-preview__identity">
          <p className="ds-file-preview__name">
            {resolvedName ?? 'No file selected'}
          </p>
          {resolvedMeta ? (
            <p className="ds-file-preview__meta">{resolvedMeta}</p>
          ) : null}
        </div>
        <div className="ds-file-preview__header-actions">
          {headerActions}
          <button
            type="button"
            className="ds-file-preview__icon-btn"
            aria-label="Close"
            tabIndex={isPreview ? -1 : undefined}
            onClick={() => {
              if (!isPreview) onClose?.()
            }}
          >
            <i className="mgc mgc_close_line" aria-hidden />
          </button>
        </div>
      </header>

      <div className="ds-file-preview__body">
        {resolvedContent.kind === 'empty' ? (
          <div className="ds-file-preview__empty">
            <i className="mgc mgc_pic_fill" aria-hidden />
            <p>No file selected</p>
            <span>Select a file from the grid to preview it here.</span>
          </div>
        ) : null}

        {resolvedContent.kind === 'image' ? (
          <img
            className="ds-file-preview__image"
            src={resolvedContent.src}
            alt={resolvedContent.alt ?? ''}
            style={{ transform: `scale(${zoom / 100})` }}
          />
        ) : null}

        {resolvedContent.kind === 'pdf' ? (
          <iframe
            className="ds-file-preview__pdf"
            title={resolvedName ?? 'PDF'}
            src={resolvedContent.src}
          />
        ) : null}

        {resolvedContent.kind === 'placeholder' ? (
          <div
            className={`ds-file-preview__placeholder ds-file-preview__placeholder--${resolvedContent.fileType}`}
          >
            <i
              className={`mgc ${TYPE_ICON[resolvedContent.fileType]}`}
              aria-hidden
            />
            <p>{resolvedContent.fileName ?? 'Preview unavailable'}</p>
          </div>
        ) : null}

        {resolvedContent.kind === 'document' ? (
          resolvedContent.loading ? (
            <p className="ds-file-preview__loading">Loading…</p>
          ) : resolvedContent.error ? (
            <p className="ds-file-preview__error">{resolvedContent.error}</p>
          ) : (
            <div
              className="ds-file-preview__document"
              style={{ transform: `scale(${zoom / 100})` }}
              dangerouslySetInnerHTML={{
                __html: sanitizeDocumentHtml(resolvedContent.html ?? ''),
              }}
            />
          )
        ) : null}
      </div>

      <footer className="ds-file-preview__footer">
        <div className="ds-file-preview__footer-left">
          <button
            type="button"
            className="ds-file-preview__icon-btn"
            aria-label="Delete"
            tabIndex={isPreview ? -1 : undefined}
            disabled={resolvedContent.kind === 'empty'}
            onClick={() => {
              if (!isPreview) onDelete?.()
            }}
          >
            <i className="mgc mgc_delete_2_line" aria-hidden />
          </button>
          <button
            type="button"
            className="ds-file-preview__icon-btn"
            aria-label="Download"
            tabIndex={isPreview ? -1 : undefined}
            disabled={resolvedContent.kind === 'empty'}
            onClick={() => {
              if (!isPreview) onDownload?.()
            }}
          >
            <i className="mgc mgc_download_2_line" aria-hidden />
          </button>
          <button
            type="button"
            className="ds-file-preview__icon-btn"
            aria-label="Details"
            tabIndex={isPreview ? -1 : undefined}
            disabled={resolvedContent.kind === 'empty'}
            onClick={() => {
              if (!isPreview) onDetails?.()
            }}
          >
            <i className="mgc mgc_information_line" aria-hidden />
          </button>
        </div>
        <div className="ds-file-preview__footer-center">
          <button
            type="button"
            className="ds-file-preview__icon-btn"
            aria-label="Zoom in"
            tabIndex={isPreview ? -1 : undefined}
            onClick={() => {
              if (!isPreview) onZoomIn?.()
            }}
          >
            <i className="mgc mgc_add_line" aria-hidden />
          </button>
          <span className="ds-file-preview__zoom">{zoom}%</span>
          <button
            type="button"
            className="ds-file-preview__icon-btn"
            aria-label="Zoom out"
            tabIndex={isPreview ? -1 : undefined}
            onClick={() => {
              if (!isPreview) onZoomOut?.()
            }}
          >
            <i className="mgc mgc_minimize_line" aria-hidden />
          </button>
        </div>
        <div className="ds-file-preview__footer-right">
          <button
            type="button"
            className="ds-file-preview__icon-btn"
            aria-label="Previous"
            tabIndex={isPreview ? -1 : undefined}
            disabled={!hasPrev}
            onClick={() => {
              if (!isPreview) onPrev?.()
            }}
          >
            <i className="mgc mgc_left_line" aria-hidden />
          </button>
          <button
            type="button"
            className="ds-file-preview__icon-btn"
            aria-label="Next"
            tabIndex={isPreview ? -1 : undefined}
            disabled={!hasNext}
            onClick={() => {
              if (!isPreview) onNext?.()
            }}
          >
            <i className="mgc mgc_right_line" aria-hidden />
          </button>
        </div>
      </footer>
    </div>
  )
}

export default FilePreviewPanel
